import React, { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signIn, signOut, confirmSignIn, fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import {
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
  UserIcon,
  ArrowPathIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import DarkModeToggle from '../components/DarkModeToggle';
import { checkTokenExpiration } from '../utils/auth';
import { fetchUserTree, fetchUserTreeSummary } from '../services/userTreeService';

const CANVAS = {
  light: '#0f766e',
  dark: '#042f2e'
};

const fieldClassName = [
  'block h-14 w-full rounded-2xl border bg-white/90 ps-12 text-base font-medium text-slate-900',
  'border-primary-200 placeholder:text-slate-400',
  'shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]',
  'transition-[border-color,box-shadow,background-color] duration-200 ease-out hover:border-primary-400',
  'focus:border-primary-600 focus:bg-white focus:ring-4 focus:ring-primary-500/20',
  'disabled:cursor-not-allowed disabled:opacity-60',
  'dark:border-primary-400/35 dark:bg-[#0c2228] dark:text-white dark:placeholder:text-slate-400',
  'dark:shadow-none dark:hover:border-primary-300/70',
  'dark:focus:border-primary-300 dark:focus:bg-[#0c2228] dark:focus:ring-primary-300/20'
].join(' ');

const Login = ({ onLogin }) => {
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Show inactivity message or session expired message if present
  React.useEffect(() => {
    if (location.state?.message) {
      setError(location.state.message);
      // Clear the message from location state
      window.history.replaceState({}, document.title);
    } else {
      // Check for session expired query parameter
      const params = new URLSearchParams(location.search);
      if (params.get('reason') === 'session_expired') {
        setError('Your session has expired. Please log in again.');
        // Clear the query parameter
        window.history.replaceState({}, document.title, location.pathname);
      }
    }
  }, [location]);

  // Check for existing user on mount and sign them out
  React.useEffect(() => {
    const checkExistingUser = async () => {
      try {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          console.log('Found existing user, signing out...');
          await signOut({ global: true });
        }
      } catch (error) {
        // No user found, which is expected
        console.log('No existing user found');
      }
    };

    checkExistingUser();
  }, []);

  // Match the browser chrome and the area behind the notch to this screen.
  React.useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    const previousMeta = meta?.getAttribute('content');
    const previousBody = document.body.style.backgroundColor;
    const previousHtml = document.documentElement.style.backgroundColor;

    const apply = () => {
      const dark = document.documentElement.classList.contains('dark');
      const color = dark ? CANVAS.dark : CANVAS.light;
      document.body.style.backgroundColor = color;
      document.documentElement.style.backgroundColor = color;
      if (meta) meta.setAttribute('content', color);
    };

    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    return () => {
      observer.disconnect();
      document.body.style.backgroundColor = previousBody;
      document.documentElement.style.backgroundColor = previousHtml;
      if (meta && previousMeta != null) meta.setAttribute('content', previousMeta);
    };
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (isLoading || isSubmitting) {
      console.log('Preventing double submission');
      return;
    }

    setError('');
    setIsLoading(true);
    setIsSubmitting(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 50));

      const { isSignedIn, nextStep } = await signIn({
        username: username.toLowerCase(),
        password
      });

      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        const { isSignedIn: confirmed } = await confirmSignIn({
          challengeResponse: password,
          options: {
            userAttributes: {
              name: username.toLowerCase(),
              phone_number: '+1000000000'
            }
          }
        });

        if (!confirmed) {
          setError('Failed to confirm sign in');
          setIsLoading(false);
          setIsSubmitting(false);
          return;
        }
      }

      const session = await fetchAuthSession();
      const groups = session.tokens?.accessToken?.payload['cognito:groups'] || [];

      if (groups.includes('Disabled') || groups.includes('Players')) {
        await signOut();
        setError('Access denied. Players and disabled accounts are not allowed.');
        setIsLoading(false);
        setIsSubmitting(false);
        return;
      }

      // Verify token expiration
      const minutesLeft = await checkTokenExpiration();
      console.log('Initial token validity:', minutesLeft, 'minutes');

      // Warm the user-tree caches while the auth transition runs so the
      // Dashboard/Layout don't start a cold 24s fetch on mount. Fire-and-forget:
      // never await these, or we'd re-introduce the blocking wait.
      fetchUserTreeSummary().catch(() => {});
      fetchUserTree(false, { staleWhileRevalidate: true }).catch(() => {});

      await onLogin(username.toLowerCase(), session);
      navigate('/dashboard', { replace: true });

    } catch (error) {
      console.error('Auth error:', error);
      setError(error.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        setIsSubmitting(false);
      }, 100);
    }
  }, [username, password, isLoading, isSubmitting, onLogin, navigate]);

  const canSubmit = username.trim().length > 0 && password.length > 0;

  return (
    <div className="login-screen relative flex flex-col overflow-x-hidden">
      <div className="login-stage relative z-10 flex min-h-0 flex-1 flex-col">
        <header className="login-hero">
          <div className="flex justify-end">
            <DarkModeToggle className="shrink-0" />
          </div>
          <h1 className="mt-6 text-[2.15rem] font-semibold leading-none tracking-tight text-white">
            Welcome back
          </h1>
          <p className="login-lede mt-3 text-base leading-relaxed text-white/80">
            Sign in to continue to your account
          </p>
        </header>

        <section className="login-sheet">
          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col" method="post" autoComplete="on">
          {error && (
            <div
              id="login-error"
              role="alert"
              className="login-error mb-5 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 dark:border-red-900/60 dark:bg-red-950/40"
            >
              <ExclamationCircleIcon className="no-flip mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-300" />
              <p className="text-sm leading-relaxed text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          <div className="login-fields flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <label htmlFor="username" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Username
              </label>
              <div className="login-field group relative">
                <span className="pointer-events-none absolute inset-y-0 start-0 flex w-12 items-center justify-center text-slate-400 transition-colors duration-200 group-focus-within:text-primary-700 dark:text-slate-500 dark:group-focus-within:text-primary-300">
                  <UserIcon className="no-flip h-5 w-5" />
                </span>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  enterKeyHint="next"
                  required
                  disabled={isLoading}
                  aria-invalid={error ? 'true' : undefined}
                  aria-describedby={error ? 'login-error' : undefined}
                  className={`${fieldClassName} pe-4`}
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="current-password" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                Password
              </label>
              <div className="login-field group relative">
                <span className="pointer-events-none absolute inset-y-0 start-0 flex w-12 items-center justify-center text-slate-400 transition-colors duration-200 group-focus-within:text-primary-700 dark:text-slate-500 dark:group-focus-within:text-primary-300">
                  <LockClosedIcon className="no-flip h-5 w-5" />
                </span>
                <input
                  id="current-password"
                  name="current-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  enterKeyHint="go"
                  required
                  disabled={isLoading}
                  aria-invalid={error ? 'true' : undefined}
                  aria-describedby={error ? 'login-error' : undefined}
                  className={`${fieldClassName} pe-14`}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="login-reveal absolute inset-y-0 end-0 flex w-14 items-center justify-center border-0 bg-transparent p-0 text-slate-400 shadow-none transition-colors duration-200 hover:text-primary-700 focus-visible:text-primary-700 disabled:opacity-50 dark:text-slate-400 dark:hover:text-primary-300 dark:focus-visible:text-primary-300"
                  onClick={() => setShowPassword((current) => !current)}
                  disabled={isLoading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  <span className="relative h-5 w-5">
                    <EyeIcon className={`no-flip absolute inset-0 h-5 w-5 transition-opacity duration-150 ${showPassword ? 'opacity-0' : 'opacity-100'}`} />
                    <EyeSlashIcon className={`no-flip absolute inset-0 h-5 w-5 transition-opacity duration-150 ${showPassword ? 'opacity-100' : 'opacity-0'}`} />
                  </span>
                </button>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`login-submit mt-8 flex h-14 w-full touch-manipulation items-center justify-center gap-2 rounded-2xl border-0 text-base font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60${canSubmit ? ' is-ready' : ''}`}
          >
            {isLoading ? (
              <>
                <ArrowPathIcon className="no-flip h-5 w-5 animate-spin" aria-hidden="true" />
                Signing in…
              </>
            ) : (
              'Sign in'
            )}
          </button>
          </form>
        </section>
      </div>

      <style>{`
        .login-screen {
          min-height: calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px));
          background: linear-gradient(165deg, #115e59 0%, #0f766e 48%, #0d9488 100%);
        }
        .dark .login-screen {
          background: linear-gradient(165deg, #0f766e 0%, #064e48 46%, #041816 100%);
        }
        .login-hero {
          position: relative;
          overflow: hidden;
          padding: 1.15rem 1.35rem 2.85rem;
          animation: login-hero 0.65s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .login-hero::before {
          content: '';
          position: absolute;
          width: 280px;
          height: 280px;
          left: -90px;
          bottom: -150px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.18);
          animation: login-orb 7s ease-in-out infinite;
          pointer-events: none;
        }
        .login-hero > * {
          position: relative;
          z-index: 1;
        }
        .login-sheet {
          position: relative;
          z-index: 2;
          display: flex;
          flex: 1 1 auto;
          flex-direction: column;
          min-height: 0;
          margin-top: -1.4rem;
          border-radius: 28px 28px 0 0;
          background: #f7fbfa;
          padding: 1.55rem 1.25rem 1.35rem;
          box-shadow: 0 -28px 60px rgba(4, 47, 46, 0.22);
          animation: login-sheet 0.72s cubic-bezier(0.16, 1, 0.3, 1) 0.08s both;
        }
        .dark .login-sheet {
          background: #0c1a20;
          box-shadow: 0 -28px 60px rgba(0, 0, 0, 0.4);
        }
        .login-field:focus-within input {
          border-color: #0f766e;
          box-shadow: 0 0 0 4px rgba(20, 184, 166, 0.38);
          background: #fff;
        }
        .dark .login-field:focus-within input {
          border-color: #5eead4;
          box-shadow: 0 0 0 4px rgba(45, 212, 191, 0.28);
          background: #10262c;
        }
        .login-error {
          animation: login-note 0.32s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .login-submit {
          background: linear-gradient(180deg, #14b8a6 0%, #0f766e 58%, #115e59 100%);
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.28),
            0 10px 18px -14px rgba(15, 118, 110, 0.7);
          transition: transform 0.16s cubic-bezier(0.22, 1, 0.36, 1), filter 0.2s ease, box-shadow 0.28s ease;
        }
        .login-submit.is-ready {
          filter: brightness(1.08);
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.35),
            0 16px 34px -10px rgba(15, 118, 110, 0.95);
        }
        .login-submit:hover:not(:disabled) {
          filter: brightness(1.06);
        }
        .login-submit.is-ready:hover:not(:disabled) {
          filter: brightness(1.12);
        }
        .login-submit:active:not(:disabled) {
          transform: translateY(1px) scale(0.98);
        }
        .login-submit:focus-visible {
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.28),
            0 0 0 4px rgba(255, 255, 255, 0.45);
        }
        .dark .login-submit {
          background: linear-gradient(180deg, #5eead4 0%, #2dd4bf 100%);
          color: #042f2e;
        }
        .dark .login-submit.is-ready {
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.5),
            0 16px 36px -8px rgba(45, 212, 191, 0.65);
        }
        .login-screen .login-reveal,
        .dark .login-screen .login-reveal {
          background: transparent;
          border-width: 0;
          box-shadow: none;
        }
        .login-screen .login-reveal:focus-visible {
          border-radius: 999px;
          box-shadow: inset 0 0 0 2px rgba(15, 118, 110, 0.55);
        }
        .dark .login-screen .login-reveal:focus-visible {
          box-shadow: inset 0 0 0 2px rgba(94, 234, 212, 0.75);
        }
        .login-screen input {
          font-size: 16px;
        }
        .login-screen input:focus,
        .login-screen input:focus-visible,
        .login-screen button:focus,
        .login-screen button:focus-visible {
          outline: none;
        }
        .login-screen input:-webkit-autofill,
        .login-screen input:-webkit-autofill:hover,
        .login-screen input:-webkit-autofill:focus {
          -webkit-text-fill-color: #0f172a;
          caret-color: #0f172a;
          box-shadow: 0 0 0 1000px #ffffff inset;
          transition: background-color 9999s ease-out;
        }
        .dark .login-screen input:-webkit-autofill,
        .dark .login-screen input:-webkit-autofill:hover,
        .dark .login-screen input:-webkit-autofill:focus {
          -webkit-text-fill-color: #f8fafc;
          caret-color: #f8fafc;
          box-shadow: 0 0 0 1000px #10262c inset;
        }
        @media (min-width: 768px) {
          .login-screen {
            align-items: center;
            justify-content: center;
            padding: 2.5rem 1.5rem;
          }
          .login-stage {
            flex: none;
            width: min(440px, 100%);
            overflow: hidden;
            border-radius: 28px;
            box-shadow: 0 30px 80px rgba(4, 47, 46, 0.35);
          }
          .login-sheet {
            flex: none;
            margin-top: 0;
            border-radius: 0;
            box-shadow: none;
          }
        }
        @media (max-height: 640px) {
          .login-hero {
            padding: 0.7rem 1.1rem 1.7rem;
          }
          .login-hero h1 {
            margin-top: 0.85rem;
            font-size: 1.7rem;
          }
          .login-lede {
            margin-top: 0.4rem;
          }
          .login-sheet {
            padding: 1.1rem 1rem 1rem;
          }
          .login-screen input,
          .login-screen button[type="submit"] {
            height: 2.75rem;
          }
        }
        @keyframes login-hero {
          from { opacity: 0; transform: translateY(-18px); }
          to { opacity: 1; transform: none; }
        }
        @keyframes login-sheet {
          from { opacity: 0; transform: translateY(72px); }
          to { opacity: 1; transform: none; }
        }
        @keyframes login-orb {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-40px, 30px) scale(1.16); }
        }
        @keyframes login-note {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .login-hero,
          .login-hero::before,
          .login-sheet,
          .login-error {
            animation: none;
          }
          .login-submit,
          .login-submit:active:not(:disabled) {
            transform: none;
            transition: none;
          }
          .login-submit:hover:not(:disabled),
          .login-submit.is-ready,
          .login-submit.is-ready:hover:not(:disabled) {
            filter: none;
          }
        }
`}</style>
    </div>
  );
};

export default Login;
